<script setup lang="ts" generic="TValue extends string | number | boolean | null">
import { fieldStateClass, type FieldState } from '@/utils/field-state'
import { computed, useAttrs } from 'vue'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    options: { label: string; value: TValue }[]
    placeholder?: string
    ariaLabel?: string
    disabled?: boolean
    state?: FieldState | null
  }>(),
  {
    placeholder: '',
    disabled: false,
    state: null,
  },
)

const model = defineModel<TValue | null>({ default: null })

const emit = defineEmits<{
  (event: 'change', value: TValue | null): void
}>()

const attrs = useAttrs()

const selectAttrs = computed(() => {
  const { class: _class, ...rest } = attrs

  return rest
})

const selectedIndex = computed(() => {
  const index = props.options.findIndex((option) => option.value === model.value)

  return index === -1 ? '' : String(index)
})

const hasPlaceholder = computed(() => props.placeholder !== '' || selectedIndex.value === '')

const stateClass = computed(() => fieldStateClass(props.state))

/* Der Platzhalter des Selects ist eine <option> · placeholder:… aus dem Feld-Rezept greift dort nicht. */
const placeholderClass = computed(() => {
  if (selectedIndex.value !== '' || props.state === 'missing') {
    return ''
  }

  return props.state === 'reading' ? 'text-auto' : 'text-ink-3'
})

function select(event: Event): void {
  const selectedIndex = (event.target as HTMLSelectElement).value

  model.value = selectedIndex === '' ? null : props.options[Number(selectedIndex)].value
  emit('change', model.value)
}
</script>

<template>
  <div class="relative" :class="attrs.class">
    <select
      v-bind="selectAttrs"
      :value="selectedIndex"
      :aria-label="ariaLabel"
      :disabled="disabled"
      class="finest-field appearance-none pr-[calc(var(--field-px)*2+var(--field-icon))]"
      :class="[stateClass, placeholderClass]"
      @change="select">
      <option v-if="hasPlaceholder" value="">{{ placeholder }}</option>
      <option v-for="(option, index) in options" :key="index" :value="String(index)">{{ option.label }}</option>
    </select>

    <i
      class="pi pi-chevron-down text-ink-3 pointer-events-none absolute top-1/2 right-[var(--field-px)] -translate-y-1/2"
      aria-hidden="true" />
  </div>
</template>
